import { useState, useEffect, useRef } from "react";
import Swipeable from "@/components/Swipeable";
import { useNavigate } from "react-router-dom";
import { 
  Trophy, Target, Calendar, TrendingUp, Award, Plus, Edit2, Trash2,
  ChevronRight, Plane, Gift, MapPin, ArrowRight, CheckCircle, Clock,
  AlertTriangle, X, Save, Building2, FileText, Percent, Upload, Loader2,
  Sparkles, FileUp, Check, ChevronDown, Menu, ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { authGet, authPost, authPut, authDelete } from "@/utils/auth";
import MobileBottomNav from "@/components/MobileBottomNav";
import MobileSidebar from "@/components/MobileSidebar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// All available insurance companies for multi-year policy rules
const ALL_INSURANCE_COMPANIES = [
  "Star Health",
  "HDFC ERGO",
  "ICICI Lombard",
  "Niva Bupa",
  "Aditya Birla",
  "Care Health",
  "ManipalCigna",
  "Bajaj Allianz",
  "Tata AIG",
  "Reliance",
  "SBI General",
  "Magma",
  "Royal Sundaram",
  "Max Bupa",
  "Future Generali",
  "Kotak",
  "Liberty",
  "Digit",
  "Go Digit",
  "Chola MS",
  "National Insurance",
  "New India Assurance",
  "United India"
];

// Prize icons mapping
const prizeIcons = {
  "paris": { icon: Plane, color: "text-purple-600", bg: "bg-purple-500/20" },
  "international": { icon: Plane, color: "text-[#2E3B57]", bg: "bg-[#EDF1F8]/40" },
  "domestic": { icon: MapPin, color: "text-emerald-600", bg: "bg-emerald-500/20" },
  "voucher": { icon: Gift, color: "text-[#A87608]", bg: "bg-[#D4A017]/15" },
  "cash": { icon: Gift, color: "text-green-600", bg: "bg-green-500/20" },
  "default": { icon: Trophy, color: "text-yellow-600", bg: "bg-yellow-500/20" }
};

const ContestTracker = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddContest, setShowAddContest] = useState(false);
  const [editingContest, setEditingContest] = useState(null);
  const [contestProgress, setContestProgress] = useState({});
  const [analyzingPdf, setAnalyzingPdf] = useState(false);
  const [showUploadOption, setShowUploadOption] = useState(true);
  const [selectedContestId, setSelectedContestId] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [auditData, setAuditData] = useState(null);
  
  // Contest form state
  const [contestForm, setContestForm] = useState({
    name: "",
    platform: "Insurance Dekho",
    insurance_type: "Health",
    start_date: "",
    end_date: "",
    booking_deadline: "",
    description: "",
    fresh_weightage: 100,
    port_weightage: 75,
    renewal_weightage: 0,
    exclude_pa: true,
    exclude_cancelled: true,
    multi_year_full_premium_insurers: ["Star Health", "ICICI Lombard", "Niva Bupa", "Aditya Birla", "HDFC ERGO", "ManipalCigna", "Reliance", "Care Health"],
    boosters: [],
    slabs: []
  });

  useEffect(() => {
    fetchContests();
  }, []);

  const fetchContests = async () => {
    try {
      setLoading(true);
      const res = await authGet('/api/advisor/contests');
      if (res.ok) {
        const contestsList = res.data.contests || [];
        setContests(contestsList);
        // Auto-select first contest if none selected
        if (contestsList.length > 0 && !selectedContestId) {
          setSelectedContestId(contestsList[0].id);
        }
        // Fetch progress for each contest
        for (const contest of contestsList) {
          fetchContestProgress(contest.id);
        }
      }
    } catch (error) {
      console.error("Error fetching contests:", error);
      toast.error("Failed to load contests");
    } finally {
      setLoading(false);
    }
  };

  const fetchContestProgress = async (contestId) => {
    try {
      const res = await authGet(`/api/advisor/contests/${contestId}/progress`);
      if (res.ok) {
        setContestProgress(prev => ({
          ...prev,
          [contestId]: res.data
        }));
      }
    } catch (error) {
      console.error("Error fetching contest progress:", error);
    }
  };

  const handleSaveContest = async () => {
    if (!contestForm.name || !contestForm.start_date || !contestForm.end_date) {
      toast.error("Contest name and dates are required");
      return;
    }

    try {
      let res;
      if (editingContest) {
        res = await authPut(`/api/advisor/contests/${editingContest.id}`, contestForm);
      } else {
        res = await authPost('/api/advisor/contests', contestForm);
      }

      if (res.ok) {
        toast.success(editingContest ? "Contest updated!" : "Contest created!");
        setShowAddContest(false);
        setEditingContest(null);
        resetForm();
        fetchContests();
      } else {
        toast.error(res.data?.detail || "Failed to save contest");
      }
    } catch (error) {
      console.error("Error saving contest:", error);
      toast.error("Failed to save contest");
    }
  };

  const handleDeleteContest = async (contestId) => {
    if (!window.confirm("Are you sure you want to delete this contest?")) return;
    
    try {
      const res = await authDelete(`/api/advisor/contests/${contestId}`);
      if (res.ok) {
        toast.success("Contest deleted");
        // Clear selection if deleted contest was selected
        if (selectedContestId === contestId) {
          setSelectedContestId(null);
        }
        fetchContests();
      } else {
        toast.error("Failed to delete contest");
      }
    } catch (error) {
      toast.error("Failed to delete contest");
    }
  };

  const resetForm = () => {
    setContestForm({
      name: "",
      platform: "Insurance Dekho",
      insurance_type: "Health",
      start_date: "",
      end_date: "",
      description: "",
      fresh_weightage: 100,
      port_weightage: 75,
      renewal_weightage: 0,
      exclude_pa: true,
      exclude_cancelled: true,
      multi_year_full_premium_insurers: ["Star Health", "ICICI Lombard", "Niva Bupa", "Aditya Birla", "HDFC ERGO", "ManipalCigna", "Reliance", "Care Health"],
      slabs: []
    });
    setShowUploadOption(true);
  };

  // Handle PDF upload and AI analysis
  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a PDF or image file");
      return;
    }

    setAnalyzingPdf(true);
    toast.info("Analyzing contest document with AI...", { duration: 10000 });

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Get auth token
      const token = localStorage.getItem('advisor_token');
      
      const response = await fetch(`${API_URL}/api/advisor/contests/analyze-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Populate form with extracted data
        const extracted = data.contest_data;
        setContestForm({
          name: extracted.name || "",
          platform: extracted.platform || "Insurance Dekho",
          insurance_type: extracted.insurance_type || "Health",
          start_date: extracted.start_date || "",
          end_date: extracted.end_date || "",
          booking_deadline: extracted.booking_deadline || "",
          description: extracted.description || "",
          fresh_weightage: extracted.fresh_weightage || 100,
          port_weightage: extracted.port_weightage || 75,
          renewal_weightage: extracted.renewal_weightage || 0,
          exclude_pa: extracted.exclude_pa !== false,
          exclude_cancelled: extracted.exclude_cancelled !== false,
          multi_year_full_premium_insurers: extracted.multi_year_full_premium_insurers || [],
          boosters: extracted.boosters || [],
          slabs: extracted.slabs || []
        });
        
        setShowUploadOption(false);
        toast.success("Contest details extracted successfully! Please review and save.");
      } else {
        toast.error(data.detail || "Failed to analyze document");
      }
    } catch (error) {
      console.error("Error analyzing PDF:", error);
      toast.error("Failed to analyze contest document");
    } finally {
      setAnalyzingPdf(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const openEditModal = (contest) => {
    setEditingContest(contest);
    setShowUploadOption(false);
    setContestForm({
      name: contest.name,
      platform: contest.platform || "Insurance Dekho",
      insurance_type: contest.insurance_type || "Health",
      start_date: contest.start_date,
      end_date: contest.end_date,
      booking_deadline: contest.booking_deadline || "",
      description: contest.description || "",
      fresh_weightage: contest.fresh_weightage || 100,
      port_weightage: contest.port_weightage || 75,
      renewal_weightage: contest.renewal_weightage || 0,
      exclude_pa: contest.exclude_pa !== false,
      exclude_cancelled: contest.exclude_cancelled !== false,
      multi_year_full_premium_insurers: contest.multi_year_full_premium_insurers || [],
      boosters: contest.boosters || [],
      slabs: contest.slabs || []
    });
    setShowAddContest(true);
  };

  const addSlab = () => {
    setContestForm(prev => ({
      ...prev,
      slabs: [...prev.slabs, { target: 0, prize: "", prize_type: "voucher" }]
    }));
  };

  const addBooster = () => {
    setContestForm(prev => ({
      ...prev,
      boosters: [...(prev.boosters || []), { 
        booster_name: "Early Bird Bonus",
        booster_start_date: prev.start_date || "",
        booster_end_date: "",
        extra_weightage_percent: 50,
        applicable_to: "single_year_only",
        description: ""
      }]
    }));
  };

  const updateBooster = (index, field, value) => {
    setContestForm(prev => ({
      ...prev,
      boosters: prev.boosters.map((b, i) => i === index ? { ...b, [field]: value } : b)
    }));
  };

  const removeBooster = (index) => {
    setContestForm(prev => ({
      ...prev,
      boosters: prev.boosters.filter((_, i) => i !== index)
    }));
  };

  const removeSlab = (index) => {
    setContestForm(prev => ({
      ...prev,
      slabs: prev.slabs.filter((_, i) => i !== index)
    }));
  };

  const updateSlab = (index, field, value) => {
    setContestForm(prev => ({
      ...prev,
      slabs: prev.slabs.map((slab, i) => 
        i === index ? { ...slab, [field]: field === 'target' ? parseInt(value) || 0 : value } : slab
      )
    }));
  };

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Format date to DD/MM/YYYY
  const formatDateDDMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getContestStatus = (contest) => {
    const now = new Date();
    const start = new Date(contest.start_date);
    const end = new Date(contest.end_date);
    
    if (now < start) return { status: "upcoming", label: "Upcoming", color: "text-[#2E3B57]", bg: "bg-[#EDF1F8]/40" };
    if (now > end) return { status: "ended", label: "Ended", color: "text-[#3A4557]", bg: "bg-gray-500/20" };
    return { status: "active", label: "Active", color: "text-emerald-600", bg: "bg-emerald-500/20" };
  };

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getCurrentSlab = (progress, slabs) => {
    if (!progress || !slabs) return null;
    const achieved = progress.weighted_premium || 0;
    let currentSlab = null;
    
    for (const slab of slabs) {
      if (achieved >= slab.target) {
        currentSlab = slab;
      }
    }
    return currentSlab;
  };

  const getNextSlab = (progress, slabs) => {
    if (!progress || !slabs) return slabs?.[0] || null;
    const achieved = progress.weighted_premium || 0;
    
    for (const slab of slabs) {
      if (achieved < slab.target) {
        return slab;
      }
    }
    return null; // All slabs achieved!
  };

  // Get the top/highest slab for overall progress display
  const getTopSlab = (slabs) => {
    if (!slabs || slabs.length === 0) return null;
    return slabs[slabs.length - 1]; // Last slab is the top prize
  };

  // Progress percentage towards the top slab
  const getTopSlabProgressPercentage = (progress, topSlab) => {
    if (!progress || !topSlab) return 0;
    const achieved = progress.weighted_premium || 0;
    return Math.min(100, Math.max(0, (achieved / topSlab.target) * 100));
  };

  const getProgressPercentage = (progress, nextSlab, currentSlab) => {
    if (!progress || !nextSlab) return 100;
    const achieved = progress.weighted_premium || 0;
    const startValue = currentSlab?.target || 0;
    const range = nextSlab.target - startValue;
    const progressInRange = achieved - startValue;
    return Math.min(100, Math.max(0, (progressInRange / range) * 100));
  };

  return (
    <div className="min-h-screen bg-[#F4F6FA] pb-24 md:pb-0">
      {/* Mobile Sidebar */}
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />

      {/* Mobile Header */}
      <header className="md:hidden bg-white/95 backdrop-blur-xl sticky top-0 z-40 safe-area-inset-top border-b border-[#C9D4E6]">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowSidebar(true)}
              className="p-2 -ml-2 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] active:bg-[#E2E9F4] transition-colors"
            >
              <Menu className="h-5 w-5 text-[#1B2A4A]" />
            </button>
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
              <Trophy className="h-5 w-5 text-[#1B2A4A]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#1B2A4A]">Contests</h1>
              <p className="text-xs text-[#3A4557]">Track prizes</p>
            </div>
          </div>
          
          <button
            onClick={() => { resetForm(); setEditingContest(null); setShowAddContest(true); }}
            className="p-2.5 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] active:scale-95 transition-transform"
          >
            <Plus className="h-5 w-5 text-[#1B2A4A]" />
          </button>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="hidden md:block bg-white/80 backdrop-blur-xl border-b border-[#C9D4E6] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/advisor/dashboard')}
                className="text-[#3A4557] hover:text-[#1B2A4A] transition-colors"
              >
                ← Back to Dashboard
              </button>
              <div className="h-6 w-px bg-[#E2E9F4]" />
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
                  <Trophy className="h-5 w-5 text-[#1B2A4A]" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-[#1B2A4A]">Contest Tracker</h1>
                  <p className="text-xs text-[#3A4557]">Track your contest progress & prizes</p>
                </div>
              </div>
            </div>
            
            <Button
              onClick={() => { resetForm(); setEditingContest(null); setShowAddContest(true); }}
              className="bg-gradient-to-r from-[#D4A017] to-[#D9A82C] hover:from-[#D4A017] hover:to-[#8A5A12] text-white"
              data-testid="add-contest-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Contest
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4A017]"></div>
          </div>
        ) : contests.length === 0 ? (
          <div className="text-center py-16">
            <div className="h-24 w-24 rounded-full bg-[#EDF1F8] flex items-center justify-center mx-auto mb-6">
              <Trophy className="h-12 w-12 text-[#3A4557]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1B2A4A] mb-2">No Contests Yet</h2>
            <p className="text-[#3A4557] mb-6">Add your first contest to start tracking your progress</p>
            <Button
              onClick={() => { resetForm(); setShowAddContest(true); }}
              className="bg-gradient-to-r from-[#D4A017] to-[#D9A82C]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Contest
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Contest List - sorted by most recent first */}
            {[...contests]
              .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
              .map((contest, index) => {
                const status = getContestStatus(contest);
                const isExpanded = selectedContestId === contest.id;
                const progress = contestProgress[contest.id];
                const currentSlab = getCurrentSlab(progress, contest.slabs);
                const topSlab = getTopSlab(contest.slabs);
                const topSlabProgressPct = getTopSlabProgressPercentage(progress, topSlab);
                const daysRemaining = getDaysRemaining(contest.end_date);
                const topPrizeInfo = prizeIcons[topSlab?.prize_type] || prizeIcons.default;
                
                return (
                  <div key={contest.id} className="bg-white/50 rounded-xl border border-[#C9D4E6] overflow-hidden">
                    {/* Contest Row - Clickable */}
                    <div 
                      onClick={() => setSelectedContestId(isExpanded ? null : contest.id)}
                      className={`p-4 flex items-center justify-between cursor-pointer hover:bg-[#EDF1F8]/50 transition-all ${isExpanded ? 'bg-[#EDF1F8]/30' : ''}`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`h-10 w-10 rounded-lg ${topPrizeInfo.bg} flex items-center justify-center flex-shrink-0`}>
                          <topPrizeInfo.icon className={`h-5 w-5 ${topPrizeInfo.color}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base font-semibold text-[#1B2A4A] truncate">{contest.name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${status.bg} ${status.color}`}>
                              {status.label}
                            </span>
                          </div>
                          <p className="text-xs text-[#3A4557]">
                            {formatDateDDMMYYYY(contest.start_date)} - {formatDateDDMMYYYY(contest.end_date)}
                            {status.status === 'active' && ` • ${daysRemaining} days left`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <ChevronRight className={`h-5 w-5 text-[#3A4557] transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t border-[#C9D4E6]">
                        {/* Action Buttons */}
                        <div className="px-4 py-3 bg-[#EDF1F8]/20 flex items-center gap-2 border-b border-[#C9D4E6]">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openEditModal(contest); }}
                            className="border-[#C9D4E6] text-[#3A4557] hover:text-[#1B2A4A] h-8"
                          >
                            <Edit2 className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDeleteContest(contest.id); }}
                            className="border-red-500/50 text-red-600 hover:bg-red-500/10 h-8"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>

                        {/* Progress Section */}
                        <div className="p-4 sm:p-6">
                          {/* Current Achievement */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="bg-[#EDF1F8]/50 rounded-xl p-4">
                              <p className="text-[#3A4557] text-sm mb-1">Weighted Premium</p>
                              <p className="text-2xl font-bold text-[#1B2A4A]">
                                {formatCurrency(progress?.weighted_premium || 0)}
                              </p>
                              <button
                                data-testid="contest-audit-open-btn"
                                onClick={(e) => { e.stopPropagation(); setAuditData({ contest, progress }); }}
                                className="text-xs text-[#A87608] hover:text-[#8F6A06] underline underline-offset-2 mt-1 flex items-center gap-1 transition-colors"
                              >
                                {progress?.policy_count || 0} policies counted
                                {(progress?.excluded_count || 0) > 0 && ` · ${progress.excluded_count} excluded`}
                                <ChevronRight className="h-3 w-3" />
                              </button>
                            </div>
                            
                            <div className="bg-[#EDF1F8]/50 rounded-xl p-4">
                              <p className="text-[#3A4557] text-sm mb-1">Current Prize</p>
                              {currentSlab ? (
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                                  <p className="text-lg font-semibold text-emerald-600">{currentSlab.prize}</p>
                                </div>
                              ) : (
                                <p className="text-lg text-[#3A4557]">Not qualified yet</p>
                              )}
                            </div>
                            
                            <div className="bg-[#EDF1F8]/50 rounded-xl p-4">
                              <p className="text-[#3A4557] text-sm mb-1">Top Prize Target</p>
                              {topSlab ? (
                                <>
                                  <p className="text-lg font-semibold text-[#A87608]">{topSlab.prize}</p>
                                  <p className="text-xs text-[#3A4557] mt-1">
                                    Need {formatCurrency(Math.max(0, topSlab.target - (progress?.weighted_premium || 0)))} more
                                  </p>
                                </>
                              ) : (
                                <p className="text-lg text-[#3A4557]">No slabs defined</p>
                              )}
                            </div>
                          </div>

                          {/* Progress Bar */}
                          {topSlab && (
                            <div className="mb-6">
                              <div className="flex items-center justify-between text-sm mb-2">
                                <span className="text-[#3A4557]">Progress to Top Prize</span>
                                <span className="text-[#1B2A4A] font-medium">
                                  {formatCurrency(progress?.weighted_premium || 0)} / {formatCurrency(topSlab.target)}
                                </span>
                              </div>
                              <div className="h-3 bg-[#EDF1F8] rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-[#D4A017] via-orange-500 to-red-500 rounded-full transition-all duration-500"
                                  style={{ width: `${topSlabProgressPct}%` }}
                                />
                              </div>
                              <p className="text-right text-xs text-[#3A4557] mt-1">
                                {topSlabProgressPct.toFixed(1)}% complete
                              </p>
                            </div>
                          )}

                          {/* Prize Slabs */}
                          <div>
                            <h4 className="text-sm font-semibold text-[#3A4557] mb-3">Prize Slabs</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {contest.slabs?.map((slab, idx) => {
                                const achieved = (progress?.weighted_premium || 0) >= slab.target;
                                const slabIcon = prizeIcons[slab.prize_type] || prizeIcons.default;
                                
                                return (
                                  <div
                                    key={idx}
                                    className={`p-3 rounded-xl border transition-all ${
                                      achieved 
                                        ? 'bg-emerald-500/10 border-emerald-500/30' 
                                        : 'bg-[#EDF1F8]/30 border-[#C9D4E6]'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 mb-2">
                                      <slabIcon.icon className={`h-4 w-4 ${achieved ? 'text-emerald-600' : slabIcon.color}`} />
                                      <span className={`text-xs font-medium ${achieved ? 'text-emerald-600' : 'text-[#3A4557]'}`}>
                                        {formatCurrency(slab.target)}
                                      </span>
                                      {achieved && <CheckCircle className="h-3 w-3 text-emerald-600 ml-auto" />}
                                    </div>
                                    <p className={`text-sm ${achieved ? 'text-emerald-700' : 'text-[#2E3B57]'}`}>
                                      {slab.prize}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Weightage Info */}
                          <div className="mt-6 p-4 bg-[#EDF1F8]/30 rounded-xl">
                            <h4 className="text-sm font-semibold text-[#3A4557] mb-2 flex items-center gap-2">
                              <Percent className="h-4 w-4" />
                              Premium Weightage Rules
                            </h4>
                            <div className="flex flex-wrap gap-4 text-sm">
                              <span className="text-emerald-600">Fresh: {contest.fresh_weightage}%</span>
                              <span className="text-[#2E3B57]">Port: {contest.port_weightage}%</span>
                              <span className="text-[#3A4557]">Renewal: {contest.renewal_weightage}%</span>
                              {contest.exclude_pa && <span className="text-red-600">PA: Excluded</span>}
                              {contest.exclude_cancelled && <span className="text-red-600">Cancelled: Excluded</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </main>

      {/* Add/Edit Contest Modal */}
      <Dialog open={showAddContest} onOpenChange={setShowAddContest}>
        <DialogContent className="bg-white border-[#C9D4E6] text-[#1B2A4A] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Trophy className="h-5 w-5 text-[#A87608]" />
              {editingContest ? "Edit Contest" : "Add New Contest"}
            </DialogTitle>
            <DialogDescription className="text-[#3A4557]">
              {showUploadOption && !editingContest 
                ? "Upload a contest PDF/image and let AI extract all the details"
                : "Review and configure contest rules and prize slabs"}
            </DialogDescription>
          </DialogHeader>

          {/* PDF Upload Section - Show first for new contests */}
          {showUploadOption && !editingContest && (
            <div className="py-6">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handlePdfUpload}
                className="hidden"
                id="contest-pdf-upload"
              />
              
              <div 
                onClick={() => !analyzingPdf && fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
                  ${analyzingPdf 
                    ? 'border-[#D4A017]/50 bg-[#D4A017]/10' 
                    : 'border-[#C9D4E6] hover:border-[#D4A017]/50 hover:bg-[#EDF1F8]/50'}`}
              >
                {analyzingPdf ? (
                  <div className="flex flex-col items-center gap-4">
                    <div className="h-16 w-16 rounded-full bg-[#D4A017]/15 flex items-center justify-center">
                      <Loader2 className="h-8 w-8 text-[#A87608] animate-spin" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-[#1B2A4A] mb-1">Analyzing Contest Document...</p>
                      <p className="text-sm text-[#3A4557]">AI is extracting contest details, rules, and prize slabs</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-4">
                    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#D4A017]/20 to-[#D9A82C]/20 flex items-center justify-center">
                      <Sparkles className="h-8 w-8 text-[#A87608]" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-[#1B2A4A] mb-1">Upload Contest PDF or Image</p>
                      <p className="text-sm text-[#3A4557] mb-3">
                        AI will automatically extract contest name, dates, weightage rules, and prize slabs
                      </p>
                      <div className="flex items-center justify-center gap-2 text-[#A87608] text-sm">
                        <FileUp className="h-4 w-4" />
                        Click to upload (PDF, PNG, JPG)
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center gap-4">
                <div className="flex-1 h-px bg-[#E2E9F4]" />
                <span className="text-[#3A4557] text-sm">OR</span>
                <div className="flex-1 h-px bg-[#E2E9F4]" />
              </div>

              <button
                onClick={() => setShowUploadOption(false)}
                className="mt-4 w-full py-3 rounded-xl border border-[#C9D4E6] text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#EDF1F8] transition-all flex items-center justify-center gap-2"
              >
                <Edit2 className="h-4 w-4" />
                Enter Details Manually
              </button>
            </div>
          )}

          {/* Form Section - Show after upload or for edit */}
          {(!showUploadOption || editingContest) && (
          <div className="space-y-6 py-4">
            {/* Success badge if AI extracted */}
            {!editingContest && contestForm.name && (
              <div className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                <Sparkles className="h-5 w-5 text-emerald-600" />
                <span className="text-emerald-600 text-sm font-medium">AI extracted contest details - Please review and save</span>
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#3A4557] text-sm">Contest Name *</Label>
                <Input
                  value={contestForm.name}
                  onChange={(e) => setContestForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., 5 महीने में PARIS"
                  className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                  data-testid="contest-name-input"
                />
              </div>
              <div>
                <Label className="text-[#3A4557] text-sm">Platform</Label>
                <Select 
                  value={contestForm.platform} 
                  onValueChange={(value) => setContestForm(prev => ({ ...prev, platform: value }))}
                >
                  <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#C9D4E6]">
                    <SelectItem value="Insurance Dekho">Insurance Dekho</SelectItem>
                    <SelectItem value="PolicyBazaar">PolicyBazaar</SelectItem>
                    <SelectItem value="Turtlemint">Turtlemint</SelectItem>
                    <SelectItem value="RenewBuy">RenewBuy</SelectItem>
                    <SelectItem value="Beshak">Beshak</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#3A4557] text-sm">Insurance Type</Label>
                <Select 
                  value={contestForm.insurance_type || "Health"} 
                  onValueChange={(value) => setContestForm(prev => ({ ...prev, insurance_type: value }))}
                >
                  <SelectTrigger className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#C9D4E6]">
                    <SelectItem value="Health">Health Insurance</SelectItem>
                    <SelectItem value="Life">Life Insurance</SelectItem>
                    <SelectItem value="Motor">Motor Insurance</SelectItem>
                    <SelectItem value="All">All Types</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#3A4557] text-sm mb-1 block">Start Date *</Label>
                <DatePicker
                  date={contestForm.start_date}
                  onDateChange={(date) => setContestForm(prev => ({ ...prev, start_date: date }))}
                  placeholder="Select start date"
                />
              </div>
              <div>
                <Label className="text-[#3A4557] text-sm mb-1 block">End Date *</Label>
                <DatePicker
                  date={contestForm.end_date}
                  onDateChange={(date) => setContestForm(prev => ({ ...prev, end_date: date }))}
                  placeholder="Select end date"
                />
              </div>
            </div>

            {/* Weightage Rules */}
            <div className="p-4 bg-[#EDF1F8]/30 rounded-xl">
              <h4 className="font-semibold text-[#1B2A4A] mb-3 flex items-center gap-2">
                <Percent className="h-4 w-4 text-[#A87608]" />
                Premium Weightage Rules
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-[#3A4557] text-xs">Fresh Policy %</Label>
                  <Input
                    type="number"
                    value={contestForm.fresh_weightage}
                    onChange={(e) => setContestForm(prev => ({ ...prev, fresh_weightage: parseInt(e.target.value) || 0 }))}
                    className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                    min="0"
                    max="100"
                  />
                </div>
                <div>
                  <Label className="text-[#3A4557] text-xs">Port Policy %</Label>
                  <Input
                    type="number"
                    value={contestForm.port_weightage}
                    onChange={(e) => setContestForm(prev => ({ ...prev, port_weightage: parseInt(e.target.value) || 0 }))}
                    className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                    min="0"
                    max="100"
                  />
                </div>
                <div>
                  <Label className="text-[#3A4557] text-xs">Renewal %</Label>
                  <Input
                    type="number"
                    value={contestForm.renewal_weightage}
                    onChange={(e) => setContestForm(prev => ({ ...prev, renewal_weightage: parseInt(e.target.value) || 0 }))}
                    className="mt-1 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                    min="0"
                    max="100"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-4 mt-3">
                <label className="flex items-center gap-2 text-sm text-[#3A4557]">
                  <input
                    type="checkbox"
                    checked={contestForm.exclude_pa}
                    onChange={(e) => setContestForm(prev => ({ ...prev, exclude_pa: e.target.checked }))}
                    className="rounded"
                  />
                  Exclude PA Cases
                </label>
                <label className="flex items-center gap-2 text-sm text-[#3A4557]">
                  <input
                    type="checkbox"
                    checked={contestForm.exclude_cancelled}
                    onChange={(e) => setContestForm(prev => ({ ...prev, exclude_cancelled: e.target.checked }))}
                    className="rounded"
                  />
                  Exclude Cancelled
                </label>
                <label className="flex items-center gap-2 text-sm text-[#3A4557]">
                  <input
                    type="checkbox"
                    checked={contestForm.exclude_renewal !== false}
                    onChange={(e) => setContestForm(prev => ({ ...prev, exclude_renewal: e.target.checked }))}
                    className="rounded"
                  />
                  Exclude Renewals
                </label>
              </div>
            </div>

            {/* Multi-Year Policy Rules */}
            <div className="p-4 bg-[#EDF1F8]/30 rounded-xl">
              <h4 className="font-semibold text-[#1B2A4A] mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-[#2E3B57]" />
                Multi-Year Policy Rules
              </h4>
              <p className="text-[#3A4557] text-sm mb-3">
                For companies in this list, FULL multi-year premium will be counted. For others, premium will be divided by policy term.
              </p>
              <div>
                <Label className="text-[#3A4557] text-xs mb-2 block">Full Premium Insurers (Select companies)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between bg-white/70 border-[#C9D4E6] text-[#1B2A4A] hover:bg-[#EDF1F8]/50 h-auto min-h-[40px] py-2"
                    >
                      <span className="text-left flex-1 text-sm">
                        {(contestForm.multi_year_full_premium_insurers || []).length > 0 
                          ? (contestForm.multi_year_full_premium_insurers || []).join(", ")
                          : "Select insurance companies..."}
                      </span>
                      <ChevronDown className="h-4 w-4 ml-2 shrink-0 text-[#3A4557]" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0 bg-white border-[#C9D4E6]" align="start">
                    <div className="p-2 border-b border-[#C9D4E6]">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#3A4557]">
                          {(contestForm.multi_year_full_premium_insurers || []).length} selected
                        </span>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs text-[#A87608] hover:text-[#A87608] h-7"
                            onClick={() => setContestForm(prev => ({ 
                              ...prev, 
                              multi_year_full_premium_insurers: [...ALL_INSURANCE_COMPANIES]
                            }))}
                          >
                            Select All
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs text-[#3A4557] hover:text-[#2E3B57] h-7"
                            onClick={() => setContestForm(prev => ({ 
                              ...prev, 
                              multi_year_full_premium_insurers: []
                            }))}
                          >
                            Clear
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto p-2">
                      {ALL_INSURANCE_COMPANIES.map((company) => {
                        const isSelected = (contestForm.multi_year_full_premium_insurers || []).includes(company);
                        return (
                          <div
                            key={company}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                              isSelected 
                                ? 'bg-[#D4A017]/15 text-[#A87608]' 
                                : 'hover:bg-[#EDF1F8] text-[#1B2A4A]'
                            }`}
                            onClick={() => {
                              setContestForm(prev => {
                                const current = prev.multi_year_full_premium_insurers || [];
                                if (isSelected) {
                                  return { ...prev, multi_year_full_premium_insurers: current.filter(c => c !== company) };
                                } else {
                                  return { ...prev, multi_year_full_premium_insurers: [...current, company] };
                                }
                              });
                            }}
                          >
                            <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                              isSelected 
                                ? 'bg-[#D4A017] border-[#D4A017]' 
                                : 'border-[#C9D4E6]'
                            }`}>
                              {isSelected && <Check className="h-3 w-3 text-[#1B2A4A]" />}
                            </div>
                            <span className="text-sm">{company}</span>
                          </div>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <p className="text-xs text-[#3A4557] mt-2">
                Example: If a 2-year policy of ₹50,000 is logged - Star Health gets ₹50,000 counted, others get ₹25,000 counted
              </p>
            </div>

            {/* Boosters / Early Bird Bonus */}
            <div className="p-4 bg-gradient-to-r from-[#D4A017]/10 to-[#D9A82C]/10 rounded-xl border border-[#D4A017]/30">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-[#1B2A4A] flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#A87608]" />
                  Bonus / Early Bird Offers
                </h4>
                <Button
                  onClick={addBooster}
                  size="sm"
                  variant="outline"
                  className="border-[#D4A017]/50 text-[#A87608] hover:text-[#A87608] hover:bg-[#D4A017]/10"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Bonus
                </Button>
              </div>
              
              {contestForm.boosters?.length > 0 ? (
                <div className="space-y-3">
                  {contestForm.boosters.map((booster, index) => (
                    <div key={index} className="p-3 bg-[#EDF1F8]/50 rounded-lg border border-[#C9D4E6]">
                      <div className="flex items-start justify-between mb-2">
                        <Input
                          value={booster.booster_name}
                          onChange={(e) => updateBooster(index, 'booster_name', e.target.value)}
                          placeholder="Bonus Name"
                          className="bg-white/70 border-[#C9D4E6] text-[#1B2A4A] text-sm font-medium w-48"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeBooster(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-500/10 h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <Label className="text-[#3A4557] text-[10px]">Extra %</Label>
                          <Input
                            type="number"
                            value={booster.extra_weightage_percent}
                            onChange={(e) => updateBooster(index, 'extra_weightage_percent', parseInt(e.target.value) || 0)}
                            className="bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-8 text-sm"
                            min="0"
                            max="100"
                          />
                        </div>
                        <div>
                          <Label className="text-[#3A4557] text-[10px] mb-1 block">Till Date</Label>
                          <DatePicker
                            date={booster.booster_end_date}
                            onDateChange={(date) => updateBooster(index, 'booster_end_date', date)}
                            placeholder="Select date"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-[#3A4557] text-[10px]">Applies To</Label>
                          <Select
                            value={booster.applicable_to}
                            onValueChange={(v) => updateBooster(index, 'applicable_to', v)}
                          >
                            <SelectTrigger className="bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="single_year_only">Single Year Only</SelectItem>
                              <SelectItem value="all">All Policies</SelectItem>
                              <SelectItem value="fresh_only">Fresh Only</SelectItem>
                              <SelectItem value="multi_year_only">Multi Year Only</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[#3A4557] text-[10px]">Description</Label>
                          <Input
                            value={booster.description || ""}
                            onChange={(e) => updateBooster(index, 'description', e.target.value)}
                            placeholder="Details..."
                            className="bg-white/70 border-[#C9D4E6] text-[#1B2A4A] h-8 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#3A4557] text-sm text-center py-4">
                  No bonus periods added. Click "Add Bonus" if the contest has early bird offers.
                </p>
              )}
            </div>

            {/* Prize Slabs */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-[#1B2A4A] flex items-center gap-2">
                  <Award className="h-4 w-4 text-[#A87608]" />
                  Prize Slabs
                </h4>
                <Button
                  onClick={addSlab}
                  size="sm"
                  variant="outline"
                  className="border-[#C9D4E6] text-[#3A4557] hover:text-[#1B2A4A]"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Slab
                </Button>
              </div>
              
              <div className="space-y-3">
                {contestForm.slabs.map((slab, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-[#EDF1F8]/30 rounded-lg">
                    <div className="flex-1">
                      <Input
                        type="number"
                        value={slab.target}
                        onChange={(e) => updateSlab(index, 'target', e.target.value)}
                        placeholder="Target (₹)"
                        className="bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        value={slab.prize}
                        onChange={(e) => updateSlab(index, 'prize', e.target.value)}
                        placeholder="Prize description"
                        className="bg-white/70 border-[#C9D4E6] text-[#1B2A4A]"
                      />
                    </div>
                    <Select
                      value={slab.prize_type}
                      onValueChange={(value) => updateSlab(index, 'prize_type', value)}
                    >
                      <SelectTrigger className="w-32 bg-white/70 border-[#C9D4E6] text-[#1B2A4A]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#C9D4E6]">
                        <SelectItem value="voucher">Voucher</SelectItem>
                        <SelectItem value="domestic">Domestic Trip</SelectItem>
                        <SelectItem value="international">International</SelectItem>
                        <SelectItem value="paris">Paris</SelectItem>
                        <SelectItem value="default">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      onClick={() => removeSlab(index)}
                      className="p-2 text-[#3A4557] hover:text-red-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="text-[#3A4557] text-sm">Notes / Terms & Conditions</Label>
              <textarea
                value={contestForm.description}
                onChange={(e) => setContestForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Any additional rules or notes..."
                className="mt-1 w-full h-24 px-3 py-2 bg-white/70 border border-[#C9D4E6] rounded-lg text-[#1B2A4A] resize-none"
              />
            </div>
          </div>
          )}

          {/* Footer buttons - only show when form is visible */}
          {(!showUploadOption || editingContest) && (
          <div className="flex justify-between pt-4 border-t border-[#C9D4E6]">
            {/* Delete button - only show when editing */}
            {editingContest ? (
              <Button
                variant="outline"
                onClick={() => {
                  if (window.confirm("Are you sure you want to delete this contest?")) {
                    handleDeleteContest(editingContest.id);
                    setShowAddContest(false);
                    setEditingContest(null);
                    resetForm();
                  }
                }}
                className="border-red-500/50 text-red-600 hover:bg-red-500/10 hover:text-red-700"
                data-testid="delete-contest-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Contest
              </Button>
            ) : (
              <div></div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowAddContest(false); setEditingContest(null); resetForm(); }}
                className="border-[#C9D4E6] text-[#3A4557]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveContest}
                className="bg-gradient-to-r from-[#D4A017] to-[#D9A82C]"
                data-testid="save-contest-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {editingContest ? "Update Contest" : "Create Contest"}
              </Button>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Calculation Audit Modal */}
      <Dialog open={!!auditData} onOpenChange={(open) => !open && setAuditData(null)}>
        <DialogContent data-testid="contest-audit-modal" className="bg-white border-[#C9D4E6] text-[#1B2A4A] max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#A87608]" />
              Calculation Audit — {auditData?.contest?.name}
            </DialogTitle>
            <DialogDescription className="text-[#3A4557]">
              Period: {formatDateDDMMYYYY(auditData?.progress?.period?.start)} – {formatDateDDMMYYYY(auditData?.progress?.period?.end)} · Weighted = Counted Premium × Weightage % × Booster
            </DialogDescription>
          </DialogHeader>

          {/* Rules Applied */}
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-1 rounded-lg bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">Fresh: {auditData?.progress?.rules_applied?.fresh_weightage}%</span>
            <span className="px-2 py-1 rounded-lg bg-[#EDF1F8]/60 text-[#2E3B57] border border-[#C9D4E6]">Port: {auditData?.progress?.rules_applied?.port_weightage}%</span>
            <span className="px-2 py-1 rounded-lg bg-[#EDF1F8]/60 text-[#3A4557] border border-[#C9D4E6]">Renewal: {auditData?.progress?.rules_applied?.renewal_weightage}%</span>
            {auditData?.progress?.rules_applied?.exclude_pa && <span className="px-2 py-1 rounded-lg bg-red-500/10 text-red-600 border border-red-500/20">PA Excluded</span>}
            {auditData?.progress?.rules_applied?.exclude_cancelled && <span className="px-2 py-1 rounded-lg bg-red-500/10 text-red-600 border border-red-500/20">Cancelled Excluded</span>}
            {(auditData?.progress?.rules_applied?.multi_year_full_insurers || []).length > 0 && (
              <span className="px-2 py-1 rounded-lg bg-[#D4A017]/10 text-[#A87608] border border-[#D4A017]/20">
                Full multi-year premium: {auditData.progress.rules_applied.multi_year_full_insurers.join(", ")}
              </span>
            )}
          </div>

          {/* Counted Policies */}
          <div>
            <h4 className="text-sm font-semibold text-emerald-600 mb-2 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Counted Policies ({auditData?.progress?.policy_count || 0})
            </h4>
            <div className="rounded-xl border border-[#C9D4E6]"><Swipeable hint>
              <table className="w-full text-xs min-w-[760px]" data-testid="audit-counted-table">
                <thead>
                  <tr className="bg-[#EDF1F8]/50 text-[#3A4557] text-left">
                    <th className="px-3 py-2 font-medium">Policy / Plan</th>
                    <th className="px-3 py-2 font-medium">Company</th>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Type</th>
                    <th className="px-3 py-2 font-medium text-right">Premium Paid</th>
                    <th className="px-3 py-2 font-medium text-right">Counted</th>
                    <th className="px-3 py-2 font-medium text-right">Weight</th>
                    <th className="px-3 py-2 font-medium text-right">Weighted ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {(auditData?.progress?.breakdown || []).map((p, i) => (
                    <tr key={i} className="border-t border-[#C9D4E6]/60 hover:bg-[#EDF1F8]/20" data-testid={`audit-counted-row-${i}`}>
                      <td className="px-3 py-2">
                        <p className="text-[#1B2A4A] font-mono">{p.policy_number || "—"}</p>
                        <p className="text-[#3A4557]">{p.plan_name}</p>
                      </td>
                      <td className="px-3 py-2 text-[#2E3B57]">{p.insurance_company}</td>
                      <td className="px-3 py-2 text-[#2E3B57] whitespace-nowrap">{formatDateDDMMYYYY(p.payment_date || p.policy_start_date)}</td>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 rounded ${p.policy_type === "Fresh" ? "bg-emerald-500/10 text-emerald-600" : p.policy_type === "Renewal" ? "bg-gray-500/20 text-[#3A4557]" : "bg-blue-500/10 text-blue-700"}`}>
                          {p.policy_type}
                        </span>
                        {p.policy_term > 1 && <p className="text-[#A87608] mt-1">{p.policy_term}Y term</p>}
                      </td>
                      <td className="px-3 py-2 text-right text-[#2E3B57] whitespace-nowrap">₹{Number(p.original_premium || 0).toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <p className={p.counted_premium !== p.original_premium ? "text-[#A87608]" : "text-[#1B2A4A]"}>₹{Number(p.counted_premium || 0).toLocaleString('en-IN')}</p>
                        {p.multi_year_note && <p className="text-[#A87608] text-[10px]">{p.multi_year_note}</p>}
                      </td>
                      <td className="px-3 py-2 text-right text-[#2E3B57] whitespace-nowrap">
                        {p.weightage_percent}%
                        {p.booster_applied && <p className="text-emerald-600 text-[10px]">+{p.booster_applied.bonus_percent}% booster</p>}
                      </td>
                      <td className="px-3 py-2 text-right text-[#1B2A4A] font-semibold whitespace-nowrap">₹{Number(p.weighted_premium || 0).toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-[#D4A017]/40 bg-[#D4A017]/5">
                    <td colSpan={7} className="px-3 py-2 text-right font-semibold text-[#A87608]">Total Weighted Premium</td>
                    <td className="px-3 py-2 text-right font-bold text-[#A87608] whitespace-nowrap" data-testid="audit-total-weighted">
                      ₹{Number(auditData?.progress?.weighted_premium || 0).toLocaleString('en-IN')}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Swipeable></div>
          </div>

          {/* Excluded Policies */}
          {(auditData?.progress?.excluded_breakdown || []).length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-2">
                <X className="h-4 w-4" />
                Excluded Policies ({auditData.progress.excluded_breakdown.length})
              </h4>
              <div className="rounded-xl border border-red-500/20"><Swipeable hint>
                <table className="w-full text-xs min-w-[600px]" data-testid="audit-excluded-table">
                  <thead>
                    <tr className="bg-red-500/5 text-[#3A4557] text-left">
                      <th className="px-3 py-2 font-medium">Policy / Plan</th>
                      <th className="px-3 py-2 font-medium">Company</th>
                      <th className="px-3 py-2 font-medium">Date</th>
                      <th className="px-3 py-2 font-medium text-right">Premium</th>
                      <th className="px-3 py-2 font-medium">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditData.progress.excluded_breakdown.map((p, i) => (
                      <tr key={i} className="border-t border-[#C9D4E6]/60" data-testid={`audit-excluded-row-${i}`}>
                        <td className="px-3 py-2">
                          <p className="text-[#1B2A4A] font-mono">{p.policy_number || "—"}</p>
                          <p className="text-[#3A4557]">{p.plan_name}</p>
                        </td>
                        <td className="px-3 py-2 text-[#2E3B57]">{p.insurance_company}</td>
                        <td className="px-3 py-2 text-[#2E3B57] whitespace-nowrap">{formatDateDDMMYYYY(p.payment_date)}</td>
                        <td className="px-3 py-2 text-right text-[#2E3B57] whitespace-nowrap">{p.premium ? `₹${Number(String(p.premium).replace(/[₹,]/g, '') || 0).toLocaleString('en-IN')}` : "—"}</td>
                        <td className="px-3 py-2 text-red-600">{p.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Swipeable></div>
              <p className="text-[10px] text-[#3A4557] mt-2">Note: Policies outside the contest period are not shown here. Excluded list covers policies rejected by contest rules (and any policy missing a payment/start date).</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
    </div>
  );
};

export default ContestTracker;
